
import React from "react";

const benefits = [
  {
    icon: "ri-temp-hot-line",
    title: "Blocks Heat Transfer",
    desc: "R-30 rated insulation creates a thermal barrier that prevents Florida's intense heat from penetrating your living space, keeping indoor temperatures stable.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Cuts Cooling Costs 30%",
    desc: "By reducing the workload on your AC system, proper insulation can lower your monthly cooling bills by up to 30% — savings that add up year after year.",
  },
  {
    icon: "ri-leaf-line",
    title: "Eco-Friendly Materials",
    desc: "We use environmentally responsible blown-in cellulose and fiberglass insulation made from recycled materials, safe for your family and the planet.",
  },
  {
    icon: "ri-volume-mute-line",
    title: "Sound Dampening",
    desc: "Premium insulation also acts as a sound barrier, reducing outside noise from traffic, storms, and neighbors for a quieter, more peaceful home.",
  },
  {
    icon: "ri-drop-line",
    title: "Moisture Control",
    desc: "Our insulation solutions include vapor barriers that prevent moisture buildup, protecting your home from mold, mildew, and structural damage.",
  },
  {
    icon: "ri-home-heart-line",
    title: "Improved Comfort",
    desc: "Eliminate hot spots and cold drafts throughout your home. Consistent temperatures in every room mean better comfort for your entire family.",
  },
];

export default function InsulationSection() {
  return (
    <section id="attic-insulation" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center mb-20">
          {/* Image side */}
          <div className="flex-1 w-full">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden shadow-xl col-span-2">
                <img
                  src="https://public.readdy.ai/ai/img_res/63f442ab063712ebd02cb20d27589500.jpg"
                  alt="Professional attic insulation installation"
                  className="w-full h-[300px] object-cover object-top"
                />
              </div>

              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20before%20photograph%20of%20a%20poorly%20insulated%20residential%20attic%20showing%20exposed%20wooden%20rafters%20and%20thin%20old%20insulation%2C%20visible%20gaps%20and%20air%20leaks%2C%20dim%20lighting%20showing%20the%20problem%20areas%20documentary%20style%20photography%20with%20natural%20lighting&width=400&height=300&seq=insul-before-002&orientation=landscape"
                  alt="Before insulation - exposed attic"
                  className="w-full h-[220px] object-cover object-top"
                />
                <div className="bg-gray-100 px-4 py-2">
                  <p className="text-sm font-semibold text-gray-700 text-center">
                    Before
                  </p>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20after%20photograph%20of%20a%20beautifully%20insulated%20residential%20attic%20with%20thick%20even%20layer%20of%20white%20blown-in%20insulation%20covering%20the%20entire%20floor%2C%20clean%20professional%20installation%20bright%20lighting%20showing%20complete%20coverage%2C%20no%20gaps%20or%20thin%20spots%20commercial%20photography%20with%20warm%20tones&width=400&height=300&seq=insul-after-003&orientation=landscape"
                  alt="After insulation - fully covered attic"
                  className="w-full h-[220px] object-cover object-top"
                />
                <div className="bg-brand-red-light px-4 py-2">
                  <p className="text-sm font-semibold text-brand-red text-center">
                    After
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content side */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                <i className="ri-home-4-line text-orange-600 text-xl"></i>
              </div>
              <span className="text-sm font-bold text-orange-600 tracking-wider uppercase">
                Attic Insulation
              </span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The Hidden Key To Lower Energy Bills
            </h2>

            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              In Florida, your attic can reach temperatures of 150 degrees or
              more during summer. Without proper insulation, that extreme heat
              radiates directly into your living space, forcing your AC to work
              overtime and driving up energy costs.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Our R-30 premium blown-in insulation creates a powerful thermal
              barrier between your attic and living areas. The result? A cooler
              home, lower bills, and an HVAC system that lasts years longer
              because it doesn&#39;t have to work as hard.
            </p>

            <div className="bg-brand-cream rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-red-light rounded-full">
                  <i className="ri-information-line text-brand-red text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-900">Did You Know?</p>
                  <p className="text-sm text-gray-600">
                    Up to 40% of a home&#39;s energy loss occurs through the attic.
                    Proper insulation is the single most cost-effective upgrade
                    you can make.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-brand-navy">R-30</p>
                <p className="text-xs text-gray-600 mt-1">Insulation Rating</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-brand-navy">30%</p>
                <p className="text-xs text-gray-600 mt-1">Cost Savings</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-brand-navy">1 Day</p>
                <p className="text-xs text-gray-600 mt-1">Installation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="bg-brand-cream rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${benefit.icon} text-orange-600 text-2xl`}></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{benefit.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
